import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { AppLayout } from "@/components/AppLayout";
import { Heart, TrendingUp, TrendingDown, Minus, Plus, Sparkles, Loader2, BarChart3, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { MoodHeatmap } from "@/components/MoodHeatmap";
import { SpecialCategoryConsent } from "@/components/ComplianceGates";

const EMOTIONS = [
  "happy", "calm", "grateful", "excited", "hopeful",
  "sad", "anxious", "stressed", "lonely", "frustrated",
  "neutral", "tired", "confused"
];

export default function MoodPage() {
  const { token } = useAuth();
  const [history, setHistory] = useState([]);
  const [insights, setInsights] = useState(null);
  const [weeklyInsights, setWeeklyInsights] = useState(null);
  const [loadingWeekly, setLoadingWeekly] = useState(false);
  const [score, setScore] = useState(5);
  const [emotions, setEmotions] = useState([]);
  const [notes, setNotes] = useState("");
  const [logging, setLogging] = useState(false);
  const [tab, setTab] = useState("log"); // log | weekly | heatmap
  const [scConsent, setScConsent] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [histRes, insRes, conRes] = await Promise.all([
        axios.get(`${API}/mood/history?days=30`, { headers }),
        axios.get(`${API}/mood/insights`, { headers }),
        axios.get(`${API}/consent/status`, { headers })
      ]);
      setHistory(histRes.data.logs);
      setInsights(insRes.data);
      setScConsent(conRes.data.special_category?.granted === true);
    } catch (err) { console.error(err); }
  };

  const fetchWeeklyInsights = async () => {
    setLoadingWeekly(true);
    try {
      const { data } = await axios.get(`${API}/mood/weekly-insights`, { headers });
      setWeeklyInsights(data);
    } catch (err) { toast.error("Could not load weekly insights"); }
    finally { setLoadingWeekly(false); }
  };

  useEffect(() => { if (tab === "weekly" && !weeklyInsights) fetchWeeklyInsights(); }, [tab]);

  const logMood = async () => {
    setLogging(true);
    try {
      await axios.post(`${API}/mood/log`, { score, emotions, notes: notes || undefined }, { headers });
      toast.success("Mood logged");
      setNotes("");
      setEmotions([]);
      fetchData();
      if (weeklyInsights) fetchWeeklyInsights(); // Refresh weekly if loaded
    } catch (err) { toast.error("Failed to log mood"); }
    finally { setLogging(false); }
  };

  const chartData = [...history].reverse().slice(-14).map(l => ({
    date: new Date(l.logged_at).toLocaleDateString("en", { month: "short", day: "numeric" }),
    score: l.score
  }));

  const TrendIcon = insights?.trend === "improving" ? TrendingUp : insights?.trend === "declining" ? TrendingDown : Minus;

  return (
    <AppLayout>
      {scConsent === false ? (
        <div className="flex-1 overflow-y-auto p-6">
          <SpecialCategoryConsent onGranted={() => setScConsent(true)} />
        </div>
      ) : (
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">
          <div className="flex items-end justify-between">
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-medium mb-1">Mood Journal</h1>
              <p className="text-sm text-muted-foreground">Track how you feel. Patterns reveal paths to peace.</p>
            </div>
          </div>

          {/* Tab bar */}
          <div className="flex gap-1 bg-muted/30 rounded-xl p-1 w-fit">
            <button
              data-testid="mood-tab-log"
              onClick={() => setTab("log")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                tab === "log" ? "bg-card text-primary shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Heart className="w-4 h-4 inline mr-1.5" strokeWidth={1.5} />Log Mood
            </button>
            <button
              data-testid="mood-tab-weekly"
              onClick={() => setTab("weekly")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                tab === "weekly" ? "bg-card text-primary shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <BarChart3 className="w-4 h-4 inline mr-1.5" strokeWidth={1.5} />Weekly Insights
            </button>
            <button
              data-testid="mood-tab-heatmap"
              onClick={() => setTab("heatmap")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                tab === "heatmap" ? "bg-card text-primary shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <CalendarDays className="w-4 h-4 inline mr-1.5" strokeWidth={1.5} />Heatmap
            </button>
          </div>

          {tab === "log" && (
            <>
              {/* Log Mood */}
              <div className="card-omni p-6 sm:p-8">
                <h2 className="font-heading text-lg sm:text-xl font-medium mb-5">How are you feeling?</h2>
                <div className="flex items-center gap-4 sm:gap-6 mb-5">
                  <Button variant="ghost" onClick={() => setScore(Math.max(1, score - 1))} className="rounded-full w-10 h-10 p-0">
                    <Minus className="w-4 h-4" />
                  </Button>
                  <div className="flex-1 flex items-center justify-center">
                    <span data-testid="mood-score" className="font-heading text-4xl sm:text-5xl font-medium text-primary">{score}</span>
                    <span className="text-muted-foreground ml-2">/10</span>
                  </div>
                  <Button variant="ghost" onClick={() => setScore(Math.min(10, score + 1))} className="rounded-full w-10 h-10 p-0">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                <div className="flex flex-wrap gap-2 mb-5">
                  {EMOTIONS.map(e => (
                    <button
                      key={e}
                      data-testid={`emotion-${e}`}
                      onClick={() => setEmotions(prev => prev.includes(e) ? prev.filter(x => x !== e) : [...prev, e])}
                      className={`px-3 py-1.5 rounded-full text-sm border-2 transition-all capitalize ${
                        emotions.includes(e) ? "border-primary bg-primary/5 text-primary font-semibold" : "border-border/50 hover:border-primary/30"
                      }`}
                    >
                      {e}
                    </button>
                  ))}
                </div>

                <textarea
                  data-testid="mood-notes"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  className="input-omni w-full border border-input min-h-[80px] py-4 resize-none"
                  placeholder="Anything on your mind? (optional)"
                />

                <Button
                  data-testid="log-mood-btn"
                  onClick={logMood}
                  disabled={logging}
                  className="mt-4 btn-omni bg-primary text-primary-foreground w-full"
                >
                  {logging ? "..." : "Log Mood"}
                </Button>
              </div>

              {/* Summary stats */}
              {insights && insights.entries > 0 && (
                <div className="grid grid-cols-3 gap-3 sm:gap-4">
                  <div className="card-omni p-4 sm:p-6 text-center">
                    <p className="text-xs sm:text-sm text-muted-foreground mb-1">Average</p>
                    <p className="font-heading text-xl sm:text-2xl font-medium">{insights.average_score}</p>
                  </div>
                  <div className="card-omni p-4 sm:p-6 text-center">
                    <p className="text-xs sm:text-sm text-muted-foreground mb-1">Trend</p>
                    <div className="flex items-center justify-center gap-1">
                      <TrendIcon className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                      <span className="font-heading text-base sm:text-lg capitalize">{insights.trend}</span>
                    </div>
                  </div>
                  <div className="card-omni p-4 sm:p-6 text-center">
                    <p className="text-xs sm:text-sm text-muted-foreground mb-1">Dominant</p>
                    <p className="font-heading text-base sm:text-lg capitalize">{insights.dominant_emotion}</p>
                  </div>
                </div>
              )}

              {/* 14-day chart */}
              {chartData.length > 1 && (
                <div className="card-omni p-6 sm:p-8">
                  <h2 className="font-heading text-lg sm:text-xl font-medium mb-5">Your mood over time</h2>
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="moodGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis domain={[0, 10]} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                      <Tooltip />
                      <Area type="monotone" dataKey="score" stroke="hsl(var(--primary))" fill="url(#moodGrad)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </>
          )}

          {tab === "weekly" && (
            <>
              {loadingWeekly ? (
                <div className="card-omni p-12 flex flex-col items-center justify-center gap-3">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Analysing your week...</p>
                </div>
              ) : weeklyInsights ? (
                <>
                  {/* Weekly summary stats */}
                  <div className="grid grid-cols-3 gap-3 sm:gap-4">
                    <div className="card-omni p-4 sm:p-6 text-center">
                      <p className="text-xs sm:text-sm text-muted-foreground mb-1">Avg Score</p>
                      <p data-testid="weekly-avg" className="font-heading text-xl sm:text-2xl font-medium text-primary">{weeklyInsights.summary.avg_score}</p>
                    </div>
                    <div className="card-omni p-4 sm:p-6 text-center">
                      <p className="text-xs sm:text-sm text-muted-foreground mb-1">Entries</p>
                      <p data-testid="weekly-entries" className="font-heading text-xl sm:text-2xl font-medium">{weeklyInsights.summary.total_entries}</p>
                    </div>
                    <div className="card-omni p-4 sm:p-6 text-center">
                      <p className="text-xs sm:text-sm text-muted-foreground mb-1">Trend</p>
                      <div className="flex items-center justify-center gap-1">
                        {weeklyInsights.summary.trend === "improving" ? <TrendingUp className="w-4 h-4 text-green-500" /> :
                         weeklyInsights.summary.trend === "declining" ? <TrendingDown className="w-4 h-4 text-red-500" /> :
                         <Minus className="w-4 h-4 text-muted-foreground" />}
                        <span data-testid="weekly-trend" className="font-heading text-base sm:text-lg capitalize">{weeklyInsights.summary.trend}</span>
                      </div>
                    </div>
                  </div>

                  {/* Top emotions */}
                  {weeklyInsights.summary.top_emotions?.length > 0 && (
                    <div className="flex items-center gap-2 px-1">
                      <span className="text-xs text-muted-foreground">Top feelings:</span>
                      {weeklyInsights.summary.top_emotions.map(e => (
                        <span key={e} className="text-xs px-2.5 py-1 rounded-full bg-primary/10 text-primary font-medium capitalize">{e}</span>
                      ))}
                    </div>
                  )}

                  {/* Weekly bar chart */}
                  {weeklyInsights.chart_data?.length > 0 && (
                    <div className="card-omni p-6 sm:p-8">
                      <h2 className="font-heading text-lg font-medium mb-5">This Week's Mood</h2>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={weeklyInsights.chart_data}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                          <XAxis dataKey="date" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                          <YAxis domain={[0, 10]} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                          <Tooltip
                            contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "12px", fontSize: "12px" }}
                            formatter={(value, name) => [value + "/10", "Score"]}
                          />
                          <Bar dataKey="score" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} maxBarSize={40} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* AI-Powered Tips */}
                  {weeklyInsights.ai_tips?.length > 0 && (
                    <div className="card-omni p-6 sm:p-8">
                      <div className="flex items-center gap-2 mb-4">
                        <Sparkles className="w-5 h-5 text-primary" strokeWidth={1.5} />
                        <h2 className="font-heading text-lg font-medium">Personalised Tips</h2>
                      </div>
                      <div className="space-y-3">
                        {weeklyInsights.ai_tips.map((tip, i) => (
                          <div
                            key={i}
                            data-testid={`ai-tip-${i}`}
                            className="flex gap-3 items-start p-4 rounded-xl bg-primary/[0.03] border border-primary/10"
                          >
                            <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                              {i + 1}
                            </span>
                            <p className="text-sm leading-relaxed">{tip}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {weeklyInsights.chart_data?.length === 0 && (
                    <div className="card-omni p-8 text-center">
                      <p className="text-muted-foreground mb-2">Not enough data yet</p>
                      <p className="text-sm text-muted-foreground">Log your mood at least twice this week to see insights.</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="card-omni p-8 text-center">
                  <p className="text-muted-foreground">Unable to load weekly insights.</p>
                  <Button onClick={fetchWeeklyInsights} className="mt-3" variant="outline" size="sm">Try Again</Button>
                </div>
              )}
            </>
          )}

          {tab === "heatmap" && (
            <MoodHeatmap />
          )}
        </div>
      </div>
      )}
    </AppLayout>
  );
}
